import { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Link } from "react-router-dom";
import IconPdfExport from "../SvgIcons/IconPdfExport";
import api from "../../services/axios";
import { useAuth } from "../../context/AuthContext";
import "./style.css";

export default function SalesReportChart() {
  const { authUser } = useAuth();
  const colors = {
    Open: "#59a8d4",
    ReOpened: "#1c1c1c",
    InProgress: "#8a8cd9",
    Escalated: "#f5a303",
    Closed: "#4aa785",
    All: "#db3700",
  };

  const statusList = Object.keys(colors).filter((s) => s !== "All");
  const [selectedRange, setSelectedRange] = useState("12 Months");
  const [selectedStatuses, setSelectedStatuses] = useState(statusList);
  const [categories, setCategories] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedRange, selectedStatuses]);

  const fetchData = async () => {
    try {
      // const res = await api.post("list_ticket", {
      //   isAdmin: authUser?.isAdmin,
      //   Code: authUser.Code,
      // });
      const res = await api.post("list_ticket")
      const data = res.data.data || [];
      const today = new Date();
      const formatDate = (date) => date.toISOString().split("T")[0];
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      let dateKeys = [];
      const statusMap = {};
      // Initialize statusMap only for all statuses (not just selected)
      statusList.forEach((status) => (statusMap[status] = {}));

      if (["12 Months", "6 Months"].includes(selectedRange)) {
        const months = selectedRange === "12 Months" ? 12 : 6;
        for (let i = months - 1; i >= 0; i--) {
          const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
          const key = `${monthNames[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
          dateKeys.push(key);
        }

        statusList.forEach((status) => {
          dateKeys.forEach((key) => (statusMap[status][key] = 0));
        });

        data.forEach((ticket) => {
          const d = new Date(ticket.Create_Date_Time_Stamp);
          const key = `${monthNames[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
          if (statusList.includes(ticket.Status_Name) && dateKeys.includes(key)) {
            statusMap[ticket.Status_Name][key]++;
          }
        });
      } else if (selectedRange === "30 Days") {
        const totalDays = 30;
        const weekSize = 7;
        const weeks = Math.ceil(totalDays / weekSize);
        const startDate = new Date(today);
        startDate.setDate(today.getDate() - totalDays + 1);

        const formatWeekDate = (date) =>
          `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;

        const weekLabels = [];
        const weekRanges = [];

        for (let i = 0; i < weeks; i++) {
          const weekStart = new Date(startDate);
          weekStart.setDate(startDate.getDate() + i * weekSize);

          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + weekSize - 1);

          const label = formatWeekDate(weekStart);
          weekLabels.push(label);
          weekRanges.push({ start: weekStart, end: weekEnd });
        }

        // Initialize statusMap
        statusList.forEach((status) => {
          weekLabels.forEach((label) => {
            statusMap[status][label] = 0;
          });
        });

        // Fill data into weekly buckets
        data.forEach((ticket) => {
          const ticketDate = new Date(ticket.Create_Date_Time_Stamp);
          if (statusList.includes(ticket.Status_Name)) {
            for (let i = 0; i < weekRanges.length; i++) {
              const { start, end } = weekRanges[i];
              if (ticketDate >= start && ticketDate <= end) {
                statusMap[ticket.Status_Name][weekLabels[i]]++;
                break;
              }
            }
          }
        });

        dateKeys = weekLabels;

      } else if (selectedRange === "7 Days") {
        const days = 7;
        for (let i = days - 1; i >= 0; i--) {
          const d = new Date();
          d.setDate(today.getDate() - i);
          const key = formatDate(d);
          dateKeys.push(key);
        }

        statusList.forEach((status) => {
          dateKeys.forEach((key) => (statusMap[status][key] = 0));
        });

        data.forEach((ticket) => {
          const d = formatDate(new Date(ticket.Create_Date_Time_Stamp));
          if (statusList.includes(ticket.Status_Name) && dateKeys.includes(d)) {
            statusMap[ticket.Status_Name][d]++;
          }
        });
      }
      else {
        const now = new Date();

        if (selectedRange === "1 Hour") {
          const interval = 10; // 10-minute intervals
          const start = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour before now

          for (let i = 0; i <= 60; i += interval) {
            const d = new Date(start.getTime() + i * 60 * 1000);
            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0"); // month is 0-based
            const year = d.getFullYear();
            const hours24 = d.getHours();
            const minutes = String(d.getMinutes()).padStart(2, "0");
            const hours12 = hours24 % 12 || 12;
            const ampm = hours24 >= 12 ? "PM" : "AM";

            // ✅ Include date in key
            const key = `${day}/${month}/${year} ${String(hours12).padStart(2, "0")}:${minutes} ${ampm}`;
            dateKeys.push(key);
          }

          // Initialize statusMap
          statusList.forEach((status) => {
            dateKeys.forEach((key) => (statusMap[status][key] = 0));
          });

          // Fill statusMap with actual data
          data.forEach((ticket) => {
            const d = new Date(ticket.Create_Date_Time_Stamp);
            const rounded = Math.floor(d.getMinutes() / 10) * 10;
            const roundedDate = new Date(d);
            roundedDate.setMinutes(rounded, 0, 0); // Clear seconds/milliseconds

            const day = String(roundedDate.getDate()).padStart(2, "0");
            const month = String(roundedDate.getMonth() + 1).padStart(2, "0");
            const year = roundedDate.getFullYear();
            const hours24 = roundedDate.getHours();
            const minutes = String(roundedDate.getMinutes()).padStart(2, "0");
            const hours12 = hours24 % 12 || 12;
            const ampm = hours24 >= 12 ? "PM" : "AM";

            const key = `${day}/${month}/${year} ${String(hours12).padStart(2, "0")}:${minutes} ${ampm}`;

            if (statusList.includes(ticket.Status_Name) && dateKeys.includes(key)) {
              statusMap[ticket.Status_Name][key]++;
            }
          });

        } 
        
        else {
          const hours = selectedRange === "6 Hours" ? 6 : 24;

          for (let i = hours - 1; i >= 0; i--) {
            const d = new Date(now);
            d.setHours(now.getHours() - i);
            d.setMinutes(0, 0, 0); // Set to start of the hour

            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            const hours24 = d.getHours();
            const hours12 = hours24 % 12 || 12;
            const minutes = "00";
            const ampm = hours24 >= 12 ? "PM" : "AM";

            const key = `${day}/${month}/${year} ${String(hours12).padStart(2, "0")}:${minutes} ${ampm}`;
            dateKeys.push(key);
          }

          statusList.forEach((status) => {
            dateKeys.forEach((key) => (statusMap[status][key] = 0));
          });

          data.forEach((ticket) => {
            const d = new Date(ticket.Create_Date_Time_Stamp);
            d.setMinutes(0, 0, 0); // Round to top of the hour

            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            const hours24 = d.getHours();
            const hours12 = hours24 % 12 || 12;
            const minutes = "00";
            const ampm = hours24 >= 12 ? "PM" : "AM";

            const key = `${day}/${month}/${year} ${String(hours12).padStart(2, "0")}:${minutes} ${ampm}`;

            if (statusList.includes(ticket.Status_Name) && dateKeys.includes(key)) {
              statusMap[ticket.Status_Name][key]++;
            }
          });
        }
      }


      setCategories(dateKeys);

      // Create series only for selectedStatuses to show/hide lines
      // const series = selectedStatuses.map((status) => ({
      //   name: status,
      //   data: dateKeys.map((key) => statusMap[status][key] || 0),
      //   color: colors[status],
      //   fillOpacity: 0.3,
      // }));

      const series = selectedStatuses.map((status) => ({
        name: status,
        data: dateKeys.map((key) => statusMap[status][key] || 0),
        color: colors[status],
        fillColor: {
          linearGradient: [0, 0, 0, 200], // vertical gradient
          stops: [
            [0, Highcharts.color(colors[status]).setOpacity(0.2).get('rgba')],
            [1, Highcharts.color(colors[status]).setOpacity(0).get('rgba')],
          ],
        },
      }));
      // Always add "All" series for the sum of all statuses (regardless of selection)
      const allCounts = dateKeys.map((key) =>
        statusList.reduce((sum, status) => sum + (statusMap[status][key] || 0), 0)
      );
      series.unshift({
        name: "All",
        data: allCounts,
        color: colors.All,
        dashStyle: "Solid",
        lineWidth: 3,
        shadow: false,
        fillColor: {
          linearGradient: [0, 0, 0, 200],
          stops: [
            [0, Highcharts.color(colors.All).setOpacity(0.2).get('rgba')],
            [1, Highcharts.color(colors.All).setOpacity(0).get('rgba')],
          ],
        },
      });
      setChartSeries(series);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const options = {
    chart: {
      type: "areaspline",
      backgroundColor: "transparent",
      spacing: [10, 0, 0, 0],
      marginRight: 0,
    },
    title: { text: null },
    xAxis: {
      categories,
      lineColor: "#F4F4F5",
      lineWidth: 1,
      tickColor: "#F4F4F5",
      tickLength: 6,
      startOnTick: false,
      endOnTick: false,
      labels: {
        x: 30,
        style: {
          color: "#52525B",
          fontSize: "13px",
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: "500",
          lineHeight: "21px",
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: "#F4F4F5",
      min: 0,
    },
    tooltip: {
      useHTML: true,
      outside: false,
      borderWidth: 0,
      backgroundColor: 'transparent',
      shadow: false,
      formatter: function () {
        return `
      <div style="
        position: relative;
        width:130px;        
        background: white;
        padding:8px 14px;
        border-radius: 12px;
        border: 1px solid #ddd;        
        text-align: center;
        filter: drop-shadow(0 44px 44px rgba(0, 0, 0, 0.08));
        backdrop-filter: blur(1.5px);
        font-family: 'Plus Jakarta Sans', sans-serif;">        
        <div style="font-size:12px; font-weight:500; line-height:20px; color: ${this.color};">${this.series.name}</div>
        <div style="font-size:13px; font-weight:700; color:#1b1b1b; line-height:22px;">${this.y.toLocaleString()}</div>
        <!-- bottom tail triangle -->
        <div style="
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid white;
        "></div>
      </div>`;
      }
    },

    plotOptions: {
      series: {
        animation: {
          duration: 800,
          easing: "easeOutCubic",
        },
        pointPlacement: 'on',
        marker: {
          enabled: true,
          radius: 8,
          symbol: 'circle',
          fillColor: undefined,
          lineWidth: 3,
          lineColor: "#ffffff",
          states: {
            hover: {
              enabled: true,
              radius: 8,
              lineWidth: 3,
              lineColor: "#ffffff",
            }
          }
        }
      },
      areaspline: {
        fillOpacity: 0.2,
        marker: { enabled: false },
        lineWidth: 2,
      },
    },
    legend: { enabled: false },
    series: chartSeries,
    exporting: { enabled: false },
  };

  return (
    <div className="salesreportchart">
      <div className="chartheader d-flex justify-content-between align-items-center">
        <h1>
          <strong>Ticket Report</strong>
        </h1>
        <ul className="d-flex justify-content-between align-items-center">
          {["12 Months", "6 Months", "30 Days", "7 Days", "1 Day", "6 Hours", "1 Hour"].map(
            (range) => (
              <li key={range} className={selectedRange === range ? "active" : ""}>
                <Link to="#" onClick={() => setSelectedRange(range)}>
                  {range}
                </Link>
              </li>
            )
          )}
        </ul>
        <span ></span>
        {/* <Link className="pdfexport" to="#">
          <IconPdfExport /> Export PDF
        </Link> */}
      </div>
      <div className="line-chart">
        <HighchartsReact highcharts={Highcharts} options={options} />
        <div className="custom-legend">
          <div className="d-flex gap-2">
            <div className="legend-color" style={{ backgroundColor: colors.All }} />
            <span>All</span>
          </div>
          {statusList.map((status) => {
            const active = selectedStatuses.includes(status);
            return (
              <div
                key={status}
                onClick={() =>
                  setSelectedStatuses(
                    active
                      ? selectedStatuses.filter((s) => s !== status)
                      : [...selectedStatuses, status]
                  )
                }
                className={`legend-item ${active ? "active" : ""}`}
              >
                <div className="legend-color" style={{ backgroundColor: colors[status] }} />
                <span>{status}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
